import { createTheme, CssBaseline, ThemeProvider, Button, Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useParams } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
import './App.css';
import CTM_AppBar from './Components/CTM_AppBar';
import { CTM_Colors } from './Helper';
import Login from './Pages/Login';
import { MainPage } from './Pages/MainPage';
import { SessionDetailPage } from './Pages/SessionDetailPage';
import DashboardPage from './Pages/DashboardPage';
import pb from './pocketBase';

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
          <Typography variant="h5" color="error">Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary">{this.state.error?.message}</Typography>
          <Button variant="contained" onClick={() => this.setState({ hasError: false, error: null })}>Try again</Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const dark_theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: CTM_Colors.ctm_green,
    },
    background: {
      default: CTM_Colors.dark_grey, // dark grey background for darkmode
    },
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e', // color for the dialog
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
  },
});

const light_theme = createTheme({
  palette: {
    primary: {
      main: CTM_Colors.ctm_green,
    },
  },
});

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isDarkmode, setIsDarkmode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });

  // Save dark mode preference (in the localstorage) whenever it changes 
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkmode));
  }, [isDarkmode]);

  // check if the user is already logged in
  useEffect(() => {
    if (pb.authStore.isValid) {
      setIsLoggedIn(true); // User is already authenticated
      console.log('User is already authenticated');
    }
  }, []);

  const handleLoginSuccess = () => {
    setIsLoggedIn(true); // Update the login state
    console.log('User is authenticated');
  };

  const handleLogout = () => {
    pb.authStore.clear(); // Clear auth token
    setIsLoggedIn(false);
  };

  return (
    <ErrorBoundary>
    <ThemeProvider theme={isDarkmode ? dark_theme : light_theme}>
      <CssBaseline /> {/* Important! otherwise dark mode doeasnt work properly*/}
      {isLoggedIn ? (
        <>
          <Router>
            <CTM_AppBar theme={isDarkmode} onChange={setIsDarkmode} logoutHandler={handleLogout} />
            <div style={{ marginLeft: '20px', marginRight: '20px' }}>
              <Routes>
                <Route path="session/:sessionId" element={<SessionDetailPageWrapper />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/" element={<MainPage />} />
                <Route path="*" element={<Navigate to="/" />} /> {/* Fallback route */}
              </Routes>
            </div>
          </Router>
        </>
      ) : (
        <div>
          <Login onLoginSuccess={handleLoginSuccess} />
        </div>
      )}
      <ToastContainer
        position="bottom-left"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Slide}
      />
    </ThemeProvider>
    </ErrorBoundary>
  );
}

const SessionDetailPageWrapper: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <SessionDetailPage sessionId={sessionId!} />;
};

export default App;